import { AlertCircle, Lock, Shield } from 'lucide-react';
import { useState } from 'react';
import { apiClient } from '../api';

interface StagingGateProps {
  onAccessGranted: () => void;
}

export function StagingGate({ onAccessGranted }: StagingGateProps) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!password.trim()) {
      setError('Please enter a password');
      return;
    }

    setIsVerifying(true);
    try {
      const result = await apiClient.verifyStagingPassword(password);
      if (result.valid) {
        sessionStorage.setItem('staging_access_granted', 'true');
        onAccessGranted();
      } else {
        setError('Incorrect password');
        setPassword('');
      }
    } catch {
      setError('Verification failed. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#241610] via-[#3A2718] to-[#241610] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-3">
            <Shield className="h-10 w-10 text-[#A66B32]" />
            <span className="text-2xl font-bold text-white">ExpertMD</span>
          </div>
          <p className="text-[#EAE0CB]/80 text-sm">Staging Environment</p>
        </div>

        {/* Gate Card */}
        <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl p-8 shadow-2xl">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#EAE0CB]/400/20 mb-4">
              <Lock className="h-7 w-7 text-[#EAE0CB]/80" />
            </div>
            <h1 className="text-xl font-semibold text-white mb-2">Access Required</h1>
            <p className="text-sm text-[#EAE0CB]/70">
              This environment is password protected for beta testing.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError('');
                }}
                placeholder="Enter access password"
                // biome-ignore lint/a11y/noAutofocus: single-field gate screen; the password box is the only interactive element
                autoFocus
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-[#EAE0CB]/60/50 focus:outline-none focus:ring-2 focus:ring-[#A66B32]/40 focus:border-transparent transition-all"
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-300 text-sm bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={isVerifying}
              className="w-full py-3 bg-[#A66B32] hover:bg-[#EAE0CB]/400 disabled:bg-[#A66B32]/50 text-white font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#A66B32]/40 focus:ring-offset-2 focus:ring-offset-slate-900"
            >
              {isVerifying ? 'Verifying...' : 'Continue'}
            </button>
          </form>
        </div>

        <p className="text-center text-[#A66B32]/40 text-xs mt-6">
          Contact your administrator for access credentials.
        </p>
      </div>
    </div>
  );
}
